/** The tour's self-registration links (plan B3), for an operator with console_tours_manage:
 *
 *  - the header row: "Self-registration links" and **Create link now** (asks first, and says
 *    how many tour-wide links the tour has);
 *  - the filter: **Tour-wide · N** (default: the master + custom links) | **Date-specific · M**
 *    (the paginated grid), and **Show revoked**;
 *  - the states: skeleton rows sized from the tour's link count, empty, self-registration
 *    off, no owning operator, a deleted tour (revoke and restore still work), a load error
 *    with Retry;
 *  - the overlays both lists share: the ⋯ menu, the QR code, the revoke confirm (its
 *    snackbar offers Undo = Restore) and the settings dialog.
 *
 *  Every action here happens on the server at once (Draft-Or-Now): none is in the draft,
 *  and none depends on the record's mode — E moves nothing. After Create the new row is
 *  scrolled to, its Copy focused, tinted for a moment, and the snackbar offers Copy. */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, FormControlLabel, Popover, Switch, Typography } from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, type ReactNode } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { TourDetail, TourSelfregLink, TourSelfregRevokeResult } from '@/api/tours';
import { HelpLink } from '@/components/HelpLink';
import { Segmented } from '@/components/Segmented';
import { useSnackbar } from '@/components/Snackbar';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';
import { usePermissions } from '@/hooks/usePermissions';

import { DatedLinksGrid } from './DatedLinksGrid';
import { LoadError } from './LoadError';
import { LinkActionsMenu } from './LinkActionsMenu';
import { LinkSettingsDialog } from './LinkSettingsDialog';
import { addressTail } from './linkWords';
import { QrPopover } from './QrPopover';
import { selfregLinksKey, TOUR_WIDE_PAGE_SIZE, tourWideKey, useCopyLink, useLinkActions, useLinkCounts } from './queries';
import { RevokeConfirm } from './RevokeConfirm';
import { ROW_PX, TourWideList, TourWideSkeleton, type LinkRowHandlers } from './TourWideList';

type Segment = 'tour-wide' | 'dated';

interface Anchored {
  link: TourSelfregLink;
  anchor: HTMLElement;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function SelfregLinks({ detail }: { detail: TourDetail }) {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const copy = useCopyLink();
  const { restore, makeMaster, refresh } = useLinkActions(detail.id);
  const counts = useLinkCounts(detail, true);

  const [segment, setSegment] = useState<Segment>('tour-wide');
  const [showRevoked, setShowRevoked] = useState(false);
  const [menu, setMenu] = useState<Anchored | null>(null);
  const [qr, setQr] = useState<Anchored | null>(null);
  const [revoking, setRevoking] = useState<Anchored | null>(null);
  const [editing, setEditing] = useState<TourSelfregLink | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [confirmingCreate, setConfirmingCreate] = useState(false);
  const createRef = useRef<HTMLButtonElement>(null);
  // The ⋯ button the menu opened from: the revoke confirm anchors there once the menu closes,
  // and focus goes back there when the settings dialog closes.
  const menuAnchor = useRef<HTMLElement | null>(null);
  const editAnchor = useRef<{ id: string; el: HTMLElement | null }>({ id: '', el: null });
  const closeSettings = () => {
    const { id, el } = editAnchor.current;
    setEditing(null);
    // After the dialog's own focus restore (it ran on unmount, to the closed menu's item).
    window.setTimeout(() => {
      // The same button, else its successor after a re-render (list or grid), else the row.
      const target =
        (el?.isConnected ? el : null) ??
        document.querySelector<HTMLElement>(`[aria-label="Actions of link ${addressTail({ id })}"]`) ??
        document.querySelector<HTMLElement>(`[data-selfreg-link="${id}"]`);
      target?.focus();
    });
  };

  const editable = Boolean(detail.tenant) && !detail.is_deleted;
  const canCreate = editable;
  const status = showRevoked ? 'all' : 'active';

  const tourWide = useQuery({
    queryKey: tourWideKey(detail.id, status),
    queryFn: () => toursApi.listSelfregLinks(detail.id, { kind: 'undated', status, page_size: TOUR_WIDE_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const rows = tourWide.data?.results ?? [];

  // One link per confirm: a double click (or a click during the popover's exit fade)
  // lands before `isPending` re-renders the button disabled.
  const creating = useRef(false);
  const startCreate = () => {
    if (creating.current) return;
    creating.current = true;
    setConfirmingCreate(false);
    create.mutate(undefined, {
      onSettled: () => {
        creating.current = false;
      },
    });
  };
  const create = useMutation({
    mutationFn: () => toursApi.createSelfregLink(detail.id),
    onSuccess: (link) => {
      setSegment('tour-wide');
      setCreated(link.id);
      void qc.invalidateQueries({ queryKey: selfregLinksKey(detail.id) });
      snackbar.show(`Link ${addressTail(link)} created.`, 'success', {
        action: { label: 'Copy', onClick: () => copy(link) },
        autoHideDuration: 10_000,
      });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not create the link.', 'error', {
        action: { label: 'Retry', onClick: startCreate },
      }),
  });

  const onRevoked = (link: TourSelfregLink, r: TourSelfregRevokeResult) => {
    setRevoking(null);
    void refresh();
    if (!r.revoked) {
      snackbar.show(`Link ${addressTail(link)} was already revoked.`, 'info');
      return;
    }
    const takeover = !r.master_changed
      ? ''
      : r.successor_created
        ? ' A new master link was created.'
        : r.master_id
          ? ` Link ${addressTail({ id: r.master_id })} is now the master.`
          : '';
    // Undo only when restoring undoes it: a former master comes back as a custom link (the
    // successor stays the master), so after a master change the action says Restore.
    snackbar.show(`Link ${addressTail(link)} revoked — it no longer works.${takeover}`, 'success', {
      action: { label: r.master_changed ? 'Restore' : 'Undo', onClick: () => restore.mutate(r.link ?? link) },
      autoHideDuration: 10_000,
      // The way back: a stray click elsewhere must not take it away.
      ignoreClickAway: true,
    });
  };

  const handlers: LinkRowHandlers = {
    onCopy: copy,
    onQr: (link, anchor) => setQr({ link, anchor }),
    onMenu: (link, anchor) => {
      menuAnchor.current = anchor;
      setMenu({ link, anchor });
    },
    onRevoke: (link, anchor) => setRevoking({ link, anchor }),
    onRestore: (link) => restore.mutate(link),
  };
  const onFocusRequestDone = useCallback(() => setCreated(null), []);

  // The revoke confirm reads the link as the list shows it now (a refetch after a failure
  // may have made it the master).
  const revokeLink = revoking ? (rows.find((l) => l.id === revoking.link.id) ?? revoking.link) : null;

  const notes: ReactNode[] = [];
  if (detail.is_deleted) {
    notes.push(
      <Note key="deleted" icon={<DeleteOutlineIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />} data="deleted">
        This tour is deleted: it takes no new link and its links’ settings stay as they are. Revoking and restoring still
        work.
      </Note>,
    );
  } else if (!detail.tenant) {
    notes.push(
      <Note key="no-operator" icon={<InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />} data="no-operator">
        This tour has no owning operator, so it takes no self-registration link.
      </Note>,
    );
  }
  if (!detail.allow_selfreg) {
    notes.push(
      <Note key="off" icon={<InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />} data="off">
        Self-registration is off: a link opens the operator’s general page, without this tour.
      </Note>,
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 0.5 }} data-selfreg-links>
      <Box
        data-block-heading
        data-selfreg-header
        sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: HEADING_ROW_PX, minWidth: 0 }}
      >
        <Typography variant="caption" component="h4" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
          Self-registration links
        </Typography>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <HelpLink story="selfreg-links" />
        </Box>
        {canCreate && (
          <ImmediateActionButton
            ref={createRef}
            onClick={() => setConfirmingCreate(true)}
            disabled={create.isPending}
            aria-haspopup="dialog"
          >
            {create.isPending ? 'Creating…' : 'Create link now'}
          </ImmediateActionButton>
        )}
        <Popover
          open={confirmingCreate}
          anchorEl={createRef.current}
          onClose={() => setConfirmingCreate(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              role: 'dialog',
              'aria-label': 'Create a self-registration link now',
              sx: { p: 1.5, width: 340, maxWidth: '90vw', display: 'grid', gap: 1 },
            } as object,
          }}
        >
          <Typography variant="body2">
            Creates a self-registration link <b>at once</b>: anyone who has it can join this tour. The creation is
            audited, and it is not part of the draft — discarding your changes does not remove it.
          </Typography>
          <Typography variant="body2" data-create-count>
            {counts.tourWide === 0
              ? 'This tour has no tour-wide link yet.'
              : `This tour has ${plural(counts.tourWide, 'tour-wide link', 'tour-wide links')}.`}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => setConfirmingCreate(false)}>
              Cancel
            </Button>
            <ImmediateActionButton variant="contained" onClick={startCreate} disabled={create.isPending}>
              Create link now
            </ImmediateActionButton>
          </Box>
        </Popover>
      </Box>

      <Box
        data-selfreg-controls
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: HEADING_ROW_PX }}
      >
        <Segmented<Segment>
          label="Which links"
          fill={false}
          value={segment}
          onChange={setSegment}
          dataAttr="selfreg-segments"
          options={[
            { value: 'tour-wide', label: `Tour-wide · ${counts.tourWide}`, name: `Tour-wide links, ${counts.tourWide}` },
            { value: 'dated', label: `Date-specific · ${counts.dated}`, name: `Date-specific links, ${counts.dated}` },
          ]}
        />
        <FormControlLabel
          sx={{ ml: 'auto', mr: 0 }}
          control={
            <Switch
              size="small"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
              inputProps={{ role: 'switch' }}
            />
          }
          label={<Typography variant="body2">Show revoked</Typography>}
        />
      </Box>

      {notes}

      {segment === 'tour-wide' ? (
        tourWide.isError && !tourWide.data ? (
          <LoadError what="the self-registration links" onRetry={() => void tourWide.refetch()} />
        ) : tourWide.isLoading ? (
          <TourWideSkeleton rows={counts.tourWide} />
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: ROW_PX, display: 'flex', alignItems: 'center' }} data-selfreg-empty>
            No tour-wide link yet.
          </Typography>
        ) : (
          <>
            <TourWideList
              links={rows}
              handlers={handlers}
              canRestore
              restoring={restore.isPending}
              focusRequest={created}
              onFocusRequestDone={onFocusRequestDone}
            />
            {tourWide.isError && <LoadError what="the self-registration links" onRetry={() => void tourWide.refetch()} />}
            {(tourWide.data?.count ?? 0) > rows.length && (
              <Typography variant="caption" color="text.secondary">
                Showing the first {rows.length} of {tourWide.data?.count} tour-wide links.
              </Typography>
            )}
          </>
        )
      ) : (
        <DatedLinksGrid
          tourId={detail.id}
          showRevoked={showRevoked}
          canSeeBookings={hasPerm('console_bookings_view')}
          onCopy={handlers.onCopy}
          onQr={handlers.onQr}
          onMenu={handlers.onMenu}
        />
      )}

      <LinkActionsMenu
        link={menu?.link ?? null}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        availability={{
          editable,
          editReason: detail.is_deleted ? 'Not on a deleted tour' : 'The tour has no operator',
          makingMaster: makeMaster.isPending,
          restoring: restore.isPending,
        }}
        onEdit={(link) => {
          editAnchor.current = { id: link.id, el: menuAnchor.current };
          setEditing(link);
        }}
        onMakeMaster={(link) => makeMaster.mutate(link)}
        onRevoke={(link) => {
          if (menuAnchor.current) setRevoking({ link, anchor: menuAnchor.current });
        }}
        onRestore={(link) => restore.mutate(link)}
      />
      <QrPopover link={qr?.link ?? null} anchorEl={qr?.anchor ?? null} onClose={() => setQr(null)} />
      <RevokeConfirm
        tourId={detail.id}
        link={revokeLink}
        anchorEl={revoking?.anchor ?? null}
        tourWideLinks={rows}
        selfregOn={detail.allow_selfreg}
        onClose={() => setRevoking(null)}
        onRevoked={onRevoked}
        onStale={() => void refresh()}
      />
      <LinkSettingsDialog detail={detail} link={editing} onClose={closeSettings} />
    </Box>
  );
}

function Note({ icon, children, data }: { icon: ReactNode; children: ReactNode; data: string }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
      data-selfreg-note={data}
    >
      {icon}
      {children}
    </Typography>
  );
}

/** For a viewer without console_tours_manage: how many there are, never the links. */
export function SelfregLinksCount({ count }: { count: number }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-selfreg-count>
      <LockOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
      {count === 0
        ? 'No tour-wide link yet.'
        : `${plural(count, 'tour-wide link', 'tour-wide links')}. Hidden: anyone holding a link can join the tour.`}
    </Typography>
  );
}
